import React from 'react';

import glassData from '../../../../../data/glassData.js';
import TEXT from '../../../../../data/text.json';

import FormController from '../../form-components/form-controller';
import OverlayWrapper from '../../form-components/overlay-wrapper';
import FormWrapper from '../../form-components/form-wrapper';
import StageProgress from '../../form-components/stage-progress';

import { validateEmail } from '../../formValidator';

import EditItemFormStageOne from './stage-1';
import EditItemFormStageTwo from './stage-2';
import EditItemFormStageThree from './stage-3';
import EditItemFormStageFour from './stage-4';
import EditItemFormStageFive from './stage-5';

const formStages = {
  1: EditItemFormStageOne,
  2: EditItemFormStageTwo,
  3: EditItemFormStageThree,
  4: EditItemFormStageFour,
  5: EditItemFormStageFive,
};

export default class EditItemForm extends FormController {
  constructor(props) {
    super(
      props,
      [
        // Stage One
        'itemNameValue',
        'itemDescValue',

        // Stage Two
        'locationValue',
        'yearValue',
        'designerValue',
        'productionMethodValue',
        'productionLocationValue',

        'tablewareTypeValue',
        'patternNumberValue',
        'colorValue',

        // Stage Three
        'mainImageValue',
        'images',

        // Stage Four
        'relatedItems',

        // Stage Five
        'emailValue',
      ],
      null,
      5,
    );

    Object.assign(this.state, {
      stage: this.state.stage || 1,
    });

    this.hasExistingImage = !!glassData.find(({ id }) => id === props.itemId)
      .mainImage;

    if (!props.recoveryState) {
      // Load details of item to fill form values
      const item = glassData.find(({ id }) => id === props.itemId);
      this.itemType = item.data.glassType;

      // Stage One
      this.state.itemNameValue = item.title;
      this.state.itemDescValue = item.description;

      // Stage Two
      this.state.locationValue = item.data.location;
      this.state.yearValue = item.data.year;
      this.state.designerValue = item.data.designer;
      this.state.productionMethodValue = item.data.productionMethod;
      this.state.productionLocationValue = item.data.productionLocation;

      this.state.tablewareTypeValue = item.data.tablewareType;
      this.state.patternNumberValue = item.data.patternNumber;
      this.state.colorValue = item.data.colour;

      // Stage Three
      //  - Existing images not yet loaded

      // Stage Four
      this.state.relatedItems = item.relatedItems;
    }
  }

  moveToStageOne = () => this.setState({ stage: 1 });
  moveToStageTwo = () => this.setState({ stage: 2 });
  moveToStageThree = () => this.setState({ stage: 3 });
  moveToStageFour = () => this.setState({ stage: 4 });

  editFormValid = () => {
    const fields = this.extractFields();
    const item = glassData.find(({ id }) => id === this.props.itemId);

    // Check that at least one value has been edited
    return (
      !!fields.email &&
      validateEmail(fields.email) &&
      !!fields.itemName &&
      (fields.itemName !== item.title ||
        fields.itemDesc !== item.description ||
        fields.location !== item.data.location ||
        fields.year !== item.data.year ||
        fields.designer !== item.data.designer ||
        fields.productionMethod !== item.data.productionMethod ||
        fields.productionLocation !== item.data.productionLocation ||
        fields.tablewareType !== item.data.tablewareType ||
        fields.patternNumber !== item.data.patternNumber ||
        fields.color !== item.data.colour ||
        fields.relatedItems !== item.relatedItems.join(',') ||
        (fields.mainImage !== null && fields.mainImage !== '') ||
        this.state.images.length > 0)
    );
  };

  editFormValidationMessage = () => {
    const fields = this.extractFields();

    if (!fields.email) {
      return 'Please enter your email address';
    } else if (!validateEmail(fields.email)) {
      return TEXT.FORMS.invalidEmailAddress;
    } else if (!fields.itemName) {
      return 'You must provide an item name';
    } else if (!this.editFormValid()) {
      return 'You have not changed any values';
    }
  };

  handleSetImages = (images, removedMain, removedIndex) => {
    if (removedMain) {
      if (!this.hasExistingImage) {
        // If the user just removed the image with was selected as "Main" then set
        // the first image as Main if the item currently has no images
        this.setMainImageValue({ target: { value: 0 } });
      } else {
        // If the user just removed the image with was selected as "Main" then set
        // the value as null if the item currently has some images
        this.setMainImageValue({ target: { value: null } });
      }
    } else {
      // If the user just added the first image and the item currently has no
      // images then make the first image the "Main"
      if (
        this.state.images.length === 0 &&
        images.length === 1 &&
        !this.hasExistingImage
      ) {
        this.setMainImageValue({ target: { value: 0 } });
      } else {
        // If the index of the removed image was less than the index of the selected
        // main then we need to decrement the main index value
        if (removedIndex < this.state.mainImageValue) {
          this.setMainImageValue({
            target: { value: this.state.mainImageValue - 1 },
          });
        }
      }
    }
    // Pass up to the actual setImages function
    this.setImages(images);
  };

  submitWrapper = () => {
    // Only send values which have changed when compared to the original item
    const item = glassData.find(({ id }) => id === this.props.itemId);

    // Fields are all text inputs indicated by ending in the word "Value"
    const fields = this.extractFields();

    // TODO: Could probably automate these checks if the naming was more consistent
    if (fields.itemName === item.title) {
      delete fields.itemName;
    }
    if (fields.itemDesc === item.description) {
      delete fields.itemDesc;
    }
    if (fields.location === item.data.location) {
      delete fields.location;
    }
    if (fields.year === item.data.year) {
      delete fields.year;
    }
    if (fields.designer === item.data.designer) {
      delete fields.designer;
    }
    if (fields.productionMethod === item.data.productionMethod) {
      delete fields.productionMethod;
    }
    if (fields.productionLocation === item.data.productionLocation) {
      delete fields.productionLocation;
    }
    if (fields.tablewareType === item.data.tablewareType) {
      delete fields.tablewareType;
    }
    if (fields.patternNumber === item.data.patternNumber) {
      delete fields.patternNumber;
    }
    if (fields.color === item.data.colour) {
      delete fields.color;
    }
    if (fields.relatedItems === item.relatedItems.join(',')) {
      delete fields.relatedItems;
    }
    if (!fields.mainImage) {
      delete fields.mainImage;
    }

    this.props.submitForm({ fields, images: this.state.images }, this.state);
  };

  render() {
    return (
      <OverlayWrapper
        title={TEXT.CONTRIBUTION_FORMS.EDIT_ITEM.title}
        subTitle={TEXT.CONTRIBUTION_FORMS.EDIT_ITEM.subTitle}
        footerProps={this.getFooterProps(
          this.submitWrapper,
          !this.editFormValid(),
          this.editFormValidationMessage(),
        )}
        stages={[
          {
            name: 'Description',
            active: this.state.stage === 1,
            onClick: () => this.setState({ stage: 1 }),
          },
          {
            name: 'Details',
            active: this.state.stage === 2,
            onClick: () => this.setState({ stage: 2 }),
          },
          {
            name: 'Images',
            active: this.state.stage === 3,
            onClick: () => this.setState({ stage: 3 }),
          },
          {
            name: 'Related',
            active: this.state.stage === 4,
            onClick: () => this.setState({ stage: 4 }),
          },
          {
            name: 'Finish',
            active: this.state.stage === 5,
            onClick: () => this.setState({ stage: 5 }),
          },
        ]}
      >
        <FormWrapper nested>
          {React.createElement(formStages[this.state.stage], {
            // Stage One values
            itemNameValue: this.state.itemNameValue,
            setItemNameValue: this.setItemNameValue,
            itemTypeValue: this.itemType,
            itemDescValue: this.state.itemDescValue,
            setItemDescValue: this.setItemDescValue,

            // Stage Two values
            locationValue: this.state.locationValue,
            setLocationValue: this.setLocationValue,
            yearValue: this.state.yearValue,
            setYearValue: this.setYearValue,
            productionMethodValue: this.state.productionMethodValue,
            setProductionMethodValue: this.setProductionMethodValue,
            productionLocationValue: this.state.productionLocationValue,
            setProductionLocationValue: this.setProductionLocationValue,
            tablewareTypeValue: this.state.tablewareTypeValue,
            setTablewareTypeValue: this.setTablewareTypeValue,
            patternNumberValue: this.state.patternNumberValue,
            setPatternNumberValue: this.setPatternNumberValue,
            colorValue: this.state.colorValue,
            setColorValue: this.setColorValue,
            designerValue: this.state.designerValue,
            setDesignerValue: this.setDesignerValue,

            // Stage Three values
            images: this.state.images,
            setImages: this.handleSetImages,
            requiresMainImage: !this.hasExistingImage,
            mainImageValue: this.state.mainImageValue,
            setMainImageValue: this.setMainImageValue,

            // Stage Four values
            relatedItems: this.state.relatedItems,
            setRelatedItems: this.setRelatedItems,

            // Stage Five
            emailValue: this.state.emailValue,
            setEmailValue: this.setEmailValue,
            formValid: this.editFormValid(),
            validationMessage: this.editFormValidationMessage(),
          })}
        </FormWrapper>
      </OverlayWrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/contribution-form/forms/edit-item-form/index.js